import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type { ReactElement } from "react";
import { StatusBadge, useToast, type Tone } from "@cx/ui";
import type { AiAgentSuggestionView } from "@cx/contracts";
import { can, type Permissions } from "@cx/db";
import { AI_AGENT_LABELS, formatBRL, formatDateBR, formatPct, formatQty } from "@cx/domain";
import { useSession } from "../../session";
import { errorMessage } from "../../errors";
import { dateTime } from "../../format";

/** Situação da sugestão. */
export const SUGGESTION_STATUS: Record<AiAgentSuggestionView["status"], [string, Tone]> = {
  pending: ["Pendente", "warning"],
  accepted: ["Aceita", "success"],
  rejected: ["Recusada", "secondary"],
  expired: ["Expirada", "secondary"],
};
const DELIVERY: Record<string, [string, Tone]> = {
  queued: ["Na fila de envio", "info"],
  sending: ["Em envio", "info"],
  sent: ["E-mail enviado", "success"],
  failed: ["Falha no envio", "danger"],
  skipped: ["Envio desligado", "secondary"],
};

/** Permissão de aceitar/recusar (a mesma que a API confere em `/ai/suggestions/{id}/…`). */
export function canDecide(perms: Permissions, s: AiAgentSuggestionView): boolean {
  switch (s.agent) {
    case "purchase_planner":
      return can(perms, "purchase_orders", "create");
    case "collector":
      return can(perms, "receivables", "update");
    case "anomaly":
      return can(perms, "dashboard", "read");
    default:
      return can(perms, "products", "create");
  }
}

function Details({ s }: { s: AiAgentSuggestionView }): ReactElement {
  const p = s.proposal;
  if (p.kind === "purchase_order")
    return (
      <>
        <p className="mb-2">
          Pedido de compra para <strong>{p.supplierName}</strong> · {p.items.length} item(ns) · total{" "}
          <strong>{formatBRL(p.totalCents)}</strong> · entrega em {p.leadTimeDays} dia(s), cobertura de{" "}
          {p.coverDays} dia(s)
        </p>
        <div className="table-responsive">
          <table className="table table-sm align-middle mb-0" aria-label="Itens sugeridos">
            <thead>
              <tr>
                <th scope="col">Produto</th>
                <th scope="col" className="text-end">
                  Comprar
                </th>
                <th scope="col" className="text-end">
                  Disponível
                </th>
                <th scope="col" className="text-end">
                  Venda/dia prevista
                </th>
                <th scope="col" className="text-end">
                  Preço
                </th>
                <th scope="col" className="text-end">
                  Total
                </th>
                <th scope="col">Motivo</th>
              </tr>
            </thead>
            <tbody>
              {p.items.map((i) => (
                <tr key={i.productId}>
                  <td>
                    {i.name}
                    {i.sku && <div className="small text-muted">{i.sku}</div>}
                  </td>
                  <td className="text-end">
                    {formatQty(i.qtyQ4)} {i.unit}
                  </td>
                  <td className="text-end">{formatQty(i.availableQ4)}</td>
                  <td className="text-end">{i.method === "holt" ? formatQty(i.dailyForecastQ4, 2) : "—"}</td>
                  <td className="text-end">{formatBRL(i.unitPriceCents)}</td>
                  <td className="text-end">{formatBRL(i.totalCents)}</td>
                  <td className="small">
                    {i.method === "holt" ? "Abaixo do ponto de pedido" : "Abaixo do estoque mínimo"}
                    {i.trend !== "flat" && (
                      <div className="text-muted">Tendência de {i.trend === "up" ? "alta" : "queda"}</div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    );
  if (p.kind === "collection_email")
    return (
      <>
        <p className="mb-2">
          {p.stepLabel} · <strong>{p.contactName}</strong> &lt;{p.toEmail}&gt; · {formatBRL(p.amountCents)}{" "}
          com vencimento em {formatDateBR(p.dueDate)}
          {p.history.paidCount > 0 && (
            <span className="text-muted">
              {" "}
              · histórico: {p.history.paidCount} conta(s) paga(s), atraso médio de {p.history.avgDaysLate}{" "}
              dia(s)
            </span>
          )}
        </p>
        <div className="border rounded p-2 bg-body-tertiary small" aria-label="Prévia do e-mail">
          <div className="fw-semibold mb-1">Assunto: {p.subject}</div>
          <p className="mb-2" style={{ whiteSpace: "pre-wrap" }}>
            {p.text}
          </p>
          {p.paymentLines.map((l) => (
            <div key={l} className="text-break">
              {l}
            </div>
          ))}
        </div>
      </>
    );
  if (p.kind === "anomaly")
    return (
      <>
        <p className="mb-1 fw-semibold">{p.title}</p>
        <ul className="list-inline small mb-0">
          {p.metrics.map((m) => (
            <li key={m.label} className="list-inline-item me-3">
              {m.label}:{" "}
              <strong>
                {m.value === null
                  ? "—"
                  : m.kind === "cents"
                    ? formatBRL(m.value)
                    : m.kind === "q4"
                      ? formatQty(m.value, 2)
                      : m.kind === "pct4"
                        ? formatPct(m.value)
                        : m.value}
              </strong>
            </li>
          ))}
        </ul>
      </>
    );
  const d = p.draft;
  return (
    <dl className="row small mb-0">
      <dt className="col-sm-3">Nome</dt>
      <dd className="col-sm-9">{d.name}</dd>
      {d.sku && (
        <>
          <dt className="col-sm-3">SKU</dt>
          <dd className="col-sm-9">{d.sku}</dd>
        </>
      )}
      <dt className="col-sm-3">Unidade</dt>
      <dd className="col-sm-9">{d.unit}</dd>
      <dt className="col-sm-3">GTIN</dt>
      <dd className="col-sm-9">{d.gtin ?? "—"}</dd>
      <dt className="col-sm-3">NCM</dt>
      <dd className="col-sm-9">
        {d.ncm ?? "—"}
        {d.ncmConfidence !== null && (
          <span className="text-muted"> (confiança {Math.round(d.ncmConfidence * 100)}%)</span>
        )}
      </dd>
      {d.brand && (
        <>
          <dt className="col-sm-3">Marca</dt>
          <dd className="col-sm-9">{d.brand}</dd>
        </>
      )}
      {d.priceCents !== null && (
        <>
          <dt className="col-sm-3">Preço</dt>
          <dd className="col-sm-9">{formatBRL(d.priceCents)}</dd>
        </>
      )}
      {d.attributes.length > 0 && (
        <>
          <dt className="col-sm-3">Atributos</dt>
          <dd className="col-sm-9">{d.attributes.map((a) => `${a.name}: ${a.value}`).join(" · ")}</dd>
        </>
      )}
      {d.review.length > 0 && (
        <>
          <dt className="col-sm-3">Conferir</dt>
          <dd className="col-sm-9 text-warning-emphasis">{d.review.join(", ").toUpperCase()}</dd>
        </>
      )}
    </dl>
  );
}

function Result({ s }: { s: AiAgentSuggestionView }): ReactElement | null {
  const r = s.result;
  if (!r) return null;
  if (r.kind === "purchase_order")
    return (
      <div className="small">
        Pedido de compra nº {r.number} criado.{" "}
        <Link to="/suprimentos/pedidos-de-compra/$id" params={{ id: r.purchaseOrderId }}>
          Abrir o pedido
        </Link>
      </div>
    );
  if (r.kind === "product")
    return (
      <div className="small">
        Produto cadastrado.{" "}
        <Link to="/cadastros/produtos/$id" params={{ id: r.productId }}>
          Abrir o produto
        </Link>
      </div>
    );
  if (r.kind === "acknowledged") return <div className="small text-muted">Marcado como ciente.</div>;
  const d = s.delivery ? DELIVERY[s.delivery.status] : null;
  return d ? (
    <div className="small">
      <StatusBadge tone={d[1]}>{d[0]}</StatusBadge>
      {s.delivery?.error && <span className="text-danger ms-2">{s.delivery.error}</span>}
    </div>
  ) : null;
}

/** Uma sugestão de agente: justificativa, prévia do efeito e, se pendente, Aceitar/Recusar. */
export function SuggestionCard({
  s,
  queryKey,
}: {
  s: AiAgentSuggestionView;
  queryKey: unknown[];
}): ReactElement {
  const { me, api } = useSession();
  const perms = me.permissions as Permissions;
  const qc = useQueryClient();
  const toast = useToast();
  const decide = useMutation({
    mutationFn: (decision: "accept" | "reject") =>
      api<AiAgentSuggestionView>(`/ai/suggestions/${s.id}/${decision}`, { method: "POST" }),
    onSuccess: (r) => {
      toast("success", r.status === "accepted" ? "Sugestão aceita" : "Sugestão recusada");
      void qc.invalidateQueries({ queryKey });
    },
    onError: (e: Error) => {
      toast("danger", errorMessage(e));
      void qc.invalidateQueries({ queryKey });
    },
  });
  const [label, tone] = SUGGESTION_STATUS[s.status];
  const acceptLabel =
    s.proposal.kind === "purchase_order"
      ? "Aceitar e criar o pedido"
      : s.proposal.kind === "collection_email"
        ? "Aceitar e enviar"
        : s.proposal.kind === "product"
          ? "Aceitar e cadastrar"
          : "Ciente";
  return (
    <article className="box card p-3 mb-3" aria-label={`Sugestão ${AI_AGENT_LABELS[s.agent]}`}>
      <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
        <strong>{AI_AGENT_LABELS[s.agent]}</strong>
        <StatusBadge tone={tone}>{label}</StatusBadge>
        {s.confidencePct4 !== null && (
          <span className="small text-muted">confiança {formatPct(s.confidencePct4)}</span>
        )}
        <span className="small text-muted ms-auto">{dateTime(s.createdAt)}</span>
      </div>
      {s.rationale && <p className="text-body-secondary mb-2">{s.rationale}</p>}
      <Details s={s} />
      <div className="mt-2">
        <Result s={s} />
      </div>
      {s.status === "pending" && canDecide(perms, s) && (
        <div className="d-flex gap-2 mt-3">
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={decide.isPending}
            onClick={() => decide.mutate("accept")}
          >
            {acceptLabel}
          </button>
          {s.proposal.kind !== "anomaly" && (
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              disabled={decide.isPending}
              onClick={() => decide.mutate("reject")}
            >
              Recusar
            </button>
          )}
          {s.proposal.kind === "anomaly" && (
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              disabled={decide.isPending}
              onClick={() => decide.mutate("reject")}
            >
              Descartar
            </button>
          )}
        </div>
      )}
    </article>
  );
}
